/**
 * Translation Manager - Clean, focused admin interface
 */

import { useState } from 'react'
import { Check, Clock, Eye } from 'lucide-react'
import { cn } from '../../lib/utils'

interface Task {
  id: string
  domain: string
  page: string
  language: string
  assignee: string | null
  status: 'pending' | 'in_progress' | 'review' | 'done'
  progress: number
}

const TASKS: Task[] = [
  { id: '1', domain: 'lukhas.ai', page: '/about', language: 'ES', assignee: 'Maya P.', status: 'review', progress: 100 },
  { id: '2', domain: 'lukhas.ai', page: '/pricing', language: 'DE', assignee: 'Carlos R.', status: 'in_progress', progress: 65 },
  { id: '3', domain: 'lukhas.store', page: '/products', language: 'FR', assignee: null, status: 'pending', progress: 0 },
  { id: '4', domain: 'lukhas.team', page: '/dashboard', language: 'JA', assignee: 'Yuki T.', status: 'in_progress', progress: 30 },
]

const TEAM = [
  { id: '1', name: 'Maya P.', languages: ['ES', 'EN'] },
  { id: '2', name: 'Carlos R.', languages: ['ES', 'DE', 'EN'] },
  { id: '3', name: 'Yuki T.', languages: ['JA', 'EN'] },
  { id: '4', name: 'Hans M.', languages: ['DE', 'EN', 'FR'] },
]

export default function TranslationManager() {
  const [tasks, setTasks] = useState(TASKS)
  const [filter, setFilter] = useState<'all' | 'review'>('all')

  const filtered = filter === 'review'
    ? tasks.filter(t => t.status === 'review')
    : tasks

  const reviewCount = tasks.filter(t => t.status === 'review').length

  return (
    <div className="space-y-12">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-light text-white mb-2">Translations</h1>
          <p className="text-gray-500">Manage content across all domains</p>
        </div>

        {/* Filter */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={cn(
              'px-4 py-2 rounded-lg text-sm transition-colors',
              filter === 'all' ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white'
            )}
          >
            All
          </button>
          <button
            onClick={() => setFilter('review')}
            className={cn(
              'px-4 py-2 rounded-lg text-sm transition-colors flex items-center gap-2',
              filter === 'review' ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white'
            )}
          >
            Review
            {reviewCount > 0 && (
              <span className="w-5 h-5 bg-lambda-gold text-black text-xs rounded-full flex items-center justify-center">
                {reviewCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Task List */}
      <div className="space-y-3">
        {filtered.map(task => (
          <TaskRow
            key={task.id}
            task={task}
            onApprove={() => setTasks(prev =>
              prev.map(t => t.id === task.id ? { ...t, status: 'done' as const } : t)
            )}
          />
        ))}

        {filtered.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            <Check className="w-8 h-8 mx-auto mb-3 opacity-50" />
            <p>All caught up</p>
          </div>
        )}
      </div>
    </div>
  )
}

function TaskRow({ task, onApprove }: { task: Task; onApprove: () => void }) {
  return (
    <div className={cn(
      'flex items-center gap-6 p-4 rounded-xl transition-colors',
      task.status === 'review'
        ? 'bg-lambda-gold/5 border border-lambda-gold/20'
        : 'bg-white/[0.02] border border-white/5'
    )}>
      {/* Domain + Page */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          <span className="text-white">{task.domain}</span>
          <span className="text-gray-600">{task.page}</span>
        </div>
        {task.assignee && (
          <p className="text-sm text-gray-500 mt-1">{task.assignee}</p>
        )}
      </div>

      {/* Language */}
      <span className="px-2 py-1 bg-white/5 rounded text-xs text-gray-400 font-mono">
        {task.language}
      </span>

      {/* Progress */}
      {task.status === 'in_progress' && (
        <div className="w-24 flex items-center gap-2">
          <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-lambda-gold/60 rounded-full transition-all"
              style={{ width: `${task.progress}%` }}
            />
          </div>
          <span className="text-xs text-gray-500">{task.progress}%</span>
        </div>
      )}

      {/* Status */}
      {task.status === 'pending' && (
        <span className="text-sm text-gray-500">Unassigned</span>
      )}

      {task.status === 'done' && (
        <Check className="w-5 h-5 text-emerald-500" />
      )}

      {/* Actions */}
      {task.status === 'review' && (
        <div className="flex items-center gap-2">
          <button className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={onApprove}
            className="px-3 py-1.5 bg-lambda-gold text-black text-sm rounded-lg hover:bg-lambda-gold/90 transition-colors"
          >
            Approve
          </button>
        </div>
      )}
    </div>
  )
}
